import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import LanguageSwitcher from "@/shared/components/shared/LanguageSwitcher";
import {
  LayoutDashboard, ArrowDownToLine, ArrowUpFromLine,
  Receipt, Settings, LogOut, Menu, X, Store, Bell, User,
  Server, Route, Webhook, FileText, ChevronLeft,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import NotificationDropdown from "@/shared/components/shared/NotificationDropdown";
import { motion, AnimatePresence } from "framer-motion";

/* ══════════════════════════════════════════
   GLOBAL CSS — injected once, never changes
══════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Geist+Mono:wght@400;500;600&family=Syne:wght@600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --sky: #0ea5e9;
  --sky4: #38bdf8;
  --cyan4: #22d3ee;
  --emerald: #34d399;
  --rose: #fb7185;
  --n950: #030710;
  --n900: #050a18;
  --n850: #070e1e;
  --b1: rgba(56,189,248,0.08);
  --b2: rgba(56,189,248,0.18);
  --t1: #e8f4ff;
  --t2: #a8c4e0;
  --t3: #5a82a0;
  --t4: #2e4a66;
  --t5: #1a2e44;
  --fd: 'Outfit', sans-serif;
  --fs: 'Syne', sans-serif;
  --fm: 'Geist Mono', monospace;
  --out: cubic-bezier(0.22,1,0.36,1);
  --spring: cubic-bezier(0.34,1.56,0.64,1);
}

html { scroll-behavior: smooth; }
body { background: var(--n950); color: var(--t1); font-family: var(--fd); -webkit-font-smoothing: antialiased; }
*::-webkit-scrollbar { width: 3px; height: 3px; }
*::-webkit-scrollbar-thumb { background: rgba(14,165,233,0.15); border-radius: 99px; }
*::-webkit-scrollbar-track { background: transparent; }

/* ══ SIDEBAR ══ */
.ml-sb {
  position: fixed; top: 0; left: 0; bottom: 0;
  display: flex; flex-direction: column;
  background:
    radial-gradient(ellipse 140% 35% at 50% -1%, rgba(14,165,233,0.13), transparent 55%),
    linear-gradient(180deg, #04091a 0%, #030710 55%, #020610 100%);
  border-right: 1px solid rgba(56,189,248,0.07);
  z-index: 50; overflow: hidden;
  /* Width animated via inline style — no framer, pure CSS transition */
  transition: width 0.38s cubic-bezier(0.22,1,0.36,1);
  will-change: width;
}

/* Top glow bar */
.ml-sb::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.7) 40%, rgba(56,189,248,0.7) 60%, transparent);
  z-index: 5; pointer-events: none;
  animation: sbglow 5s ease-in-out infinite;
}
@keyframes sbglow { 0%,100%{opacity:0.35} 50%{opacity:1} }

/* Bottom fade overlay */
.ml-sb::after {
  content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 90px;
  background: linear-gradient(to top, rgba(2,6,16,0.95), transparent);
  z-index: 4; pointer-events: none;
}

/* ── LOGO ── */
.ml-logo {
  height: 70px;
  display: flex; align-items: center;
  padding: 0 16px; gap: 13px;
  border-bottom: 1px solid rgba(56,189,248,0.07);
  flex-shrink: 0; text-decoration: none;
  position: relative; overflow: hidden; cursor: pointer;
  transition: background 0.22s;
  z-index: 2;
}
.ml-logo:hover { background: rgba(14,165,233,0.05); }

.ml-orb {
  width: 40px; height: 40px; border-radius: 13px;
  background: linear-gradient(135deg,#0ea5e9,#22d3ee);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; position: relative; overflow: hidden;
  box-shadow: 0 0 0 1px rgba(34,211,238,0.38), 0 4px 20px rgba(14,165,233,0.32);
  transition: box-shadow 0.3s, transform 0.4s var(--spring);
}
.ml-orb::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 48%;
  background: rgba(255,255,255,0.22); border-radius: inherit;
}
.ml-orb svg { width: 18px; height: 18px; color: #fff; position: relative; z-index: 1; }
.ml-logo:hover .ml-orb {
  box-shadow: 0 0 0 2px rgba(34,211,238,0.65), 0 6px 28px rgba(34,211,238,0.48);
  transform: scale(1.08) rotate(-7deg);
}

.ml-logo-name {
  font-family: var(--fs);
  font-size: 1.15rem; font-weight: 800; color: var(--t1);
  letter-spacing: -0.02em; white-space: nowrap; line-height: 1;
}
.ml-logo-tag {
  font-family: var(--fm);
  font-size: 0.5rem; font-weight: 500; color: var(--t4);
  text-transform: uppercase; letter-spacing: 0.24em; margin-top: 4px; white-space: nowrap;
}

/* ── NAV ── */
.ml-nav {
  flex: 1; padding: 14px 10px;
  overflow-y: auto; overflow-x: hidden;
  position: relative; z-index: 2;
}
.ml-nav::-webkit-scrollbar { width: 0; }

.ml-sec-lbl {
  font-family: var(--fm);
  font-size: 0.5rem; font-weight: 600;
  color: rgba(56,189,248,0.22);
  text-transform: uppercase; letter-spacing: 0.28em;
  padding: 0 12px; margin: 6px 0 8px; white-space: nowrap;
}
.ml-sep {
  height: 1px; margin: 8px 12px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.08), transparent);
}

/* ── NAV ITEM ── */
.ml-item {
  display: flex; align-items: center; gap: 11px;
  padding: 0 12px; height: 46px;
  border-radius: 12px; margin-bottom: 3px;
  text-decoration: none;
  font-family: var(--fd); font-size: 0.9rem; font-weight: 500;
  color: var(--t3);
  border: 1px solid transparent;
  position: relative; overflow: hidden; cursor: pointer; white-space: nowrap;
  /* NO transform here — causes blink */
  transition: color 0.2s, background 0.2s, border-color 0.2s, box-shadow 0.2s;
}

/* Shimmer sweep on hover */
.ml-item::after {
  content: '';
  position: absolute; top: 0; left: -130%; width: 70%; bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(14,165,233,0.09), transparent);
  transform: skewX(-16deg);
  transition: left 0.5s var(--out); pointer-events: none;
}
.ml-item:hover { color: var(--t1); background: rgba(14,165,233,0.07); border-color: rgba(14,165,233,0.14); }
.ml-item:hover::after { left: 160%; }

/* Active state */
.ml-item.on {
  color: #fff;
  background: linear-gradient(90deg, rgba(14,165,233,0.17) 0%, rgba(34,211,238,0.06) 100%);
  border-color: rgba(34,211,238,0.22);
  box-shadow: inset 0 1px 0 rgba(56,189,248,0.15), 0 2px 20px rgba(14,165,233,0.15);
}
.ml-item.on::after { display: none; }

/* Active accent bar */
.ml-item.on::before {
  content: '';
  position: absolute; left: 0; top: 18%; bottom: 18%;
  width: 3px; border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #38bdf8, #22d3ee, #0ea5e9);
  box-shadow: 0 0 14px rgba(34,211,238,0.95), 0 0 6px rgba(34,211,238,0.5);
}

/* Icon box */
.ml-ico {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.025);
  transition: background 0.22s, box-shadow 0.22s, transform 0.22s var(--spring);
}
.ml-ico svg { width: 15px; height: 15px; }
.ml-item:hover .ml-ico {
  background: rgba(14,165,233,0.15);
  box-shadow: 0 0 12px rgba(14,165,233,0.2);
  transform: scale(1.1);
}
.ml-item.on .ml-ico {
  background: linear-gradient(135deg, rgba(14,165,233,0.3), rgba(34,211,238,0.12));
  box-shadow: 0 2px 14px rgba(14,165,233,0.25);
}
.ml-item.on .ml-ico svg { filter: drop-shadow(0 0 4px rgba(34,211,238,0.7)); }

.ml-item-txt { flex: 1; white-space: nowrap; letter-spacing: 0.005em; }

.ml-badge {
  font-family: var(--fm);
  font-size: 0.5rem; font-weight: 600;
  padding: 2px 7px; border-radius: 99px;
  background: rgba(14,165,233,0.1); color: var(--sky4);
  border: 1px solid rgba(14,165,233,0.22);
  letter-spacing: 0.08em; text-transform: uppercase;
}

/* Tooltip (collapsed) */
.ml-tipwrap { position: relative; }
.ml-tip {
  position: absolute; left: calc(100% + 14px); top: 50%; transform: translateY(-50%);
  background: rgba(5,10,24,0.98); border: 1px solid var(--b2);
  border-radius: 10px; padding: 6px 14px;
  font-family: var(--fd); font-size: 0.82rem; font-weight: 600; color: var(--t1);
  white-space: nowrap; pointer-events: none; opacity: 0;
  transition: opacity 0.18s; z-index: 100;
  box-shadow: 0 4px 20px rgba(0,0,0,0.55), 0 0 0 1px rgba(56,189,248,0.08);
}
.ml-tip::before {
  content: ''; position: absolute; left: -5px; top: 50%;
  width: 8px; height: 8px;
  background: rgba(5,10,24,0.98);
  border-left: 1px solid var(--b2); border-bottom: 1px solid var(--b2);
  transform: translateY(-50%) rotate(45deg);
}
.ml-tipwrap:hover .ml-tip { opacity: 1; }

/* ── MERCHANT CARD ── */
.ml-mcard {
  margin: 0 10px 70px;
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(14,165,233,0.09), rgba(34,211,238,0.03));
  border: 1px solid rgba(34,211,238,0.12);
  position: relative; overflow: hidden; z-index: 2;
  transition: border-color 0.28s, box-shadow 0.28s, transform 0.28s;
}
.ml-mcard:hover {
  border-color: rgba(34,211,238,0.3);
  box-shadow: 0 4px 36px rgba(14,165,233,0.15);
  transform: translateY(-2px);
}
.ml-mcard::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.5), transparent);
}
.ml-mcard::after {
  content: ''; position: absolute; bottom: -32px; right: -32px;
  width: 120px; height: 120px; border-radius: 50%;
  background: radial-gradient(circle, rgba(34,211,238,0.1), transparent 70%);
  pointer-events: none;
}
.ml-mcard-in { padding: 16px; position: relative; z-index: 1; }
.ml-mcard-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.ml-mcard-av {
  width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, rgba(14,165,233,0.3), rgba(34,211,238,0.14));
  border: 1px solid rgba(34,211,238,0.25);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 16px rgba(34,211,238,0.16);
  transition: box-shadow 0.25s, transform 0.25s var(--spring);
}
.ml-mcard:hover .ml-mcard-av { box-shadow: 0 0 28px rgba(34,211,238,0.34); transform: scale(1.07); }
.ml-mcard-av svg { width: 18px; height: 18px; color: #22d3ee; }
.ml-mcard-lbl { font-family: var(--fm); font-size: 0.5rem; font-weight: 600; color: rgba(34,211,238,0.44); text-transform: uppercase; letter-spacing: 0.2em; margin-bottom: 3px; }
.ml-mcard-name { font-family: var(--fs); font-size: 0.9rem; font-weight: 700; color: var(--t1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: -0.01em; }
.ml-mcard-div { height: 1px; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.1), transparent); margin: 0 0 11px; }
.ml-mcard-bot { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.ml-mcard-id-lbl { font-family: var(--fm); font-size: 0.54rem; font-weight: 600; color: var(--t4); text-transform: uppercase; letter-spacing: 0.12em; }
.ml-mcard-id-val { font-family: var(--fm); font-size: 0.65rem; color: rgba(34,211,238,0.6); background: rgba(34,211,238,0.06); border: 1px solid rgba(34,211,238,0.12); border-radius: 6px; padding: 2px 8px; }
.ml-mcard-status { display: flex; align-items: center; gap: 5px; font-family: var(--fm); font-size: 0.62rem; font-weight: 600; color: var(--emerald); }
.ml-mcard-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--emerald); box-shadow: 0 0 10px rgba(52,211,153,0.85); animation: mcdot 2.4s ease-in-out infinite; }
@keyframes mcdot { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.6);opacity:0.45} }

/* ── COLLAPSE BUTTON ── */
.ml-togwrap {
  position: absolute; bottom: 20px; left: 0; right: 0;
  display: flex; justify-content: center; z-index: 6; pointer-events: none;
}
.ml-tog {
  pointer-events: all;
  display: flex; align-items: center; gap: 8px;
  padding: 8px 18px; border-radius: 99px;
  background: rgba(14,165,233,0.07);
  border: 1px solid rgba(34,211,238,0.16);
  color: var(--t3); cursor: pointer;
  position: relative; overflow: hidden;
  transition: color 0.22s, border-color 0.25s, box-shadow 0.25s, transform 0.22s, background 0.22s;
  white-space: nowrap; font-family: var(--fd);
}
.ml-tog::before { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.07), transparent); opacity: 0; transition: opacity 0.25s; }
.ml-tog:hover {
  color: #22d3ee; border-color: rgba(34,211,238,0.52);
  box-shadow: 0 0 30px rgba(34,211,238,0.24), inset 0 0 16px rgba(34,211,238,0.04);
  transform: translateY(-2px) scale(1.04);
}
.ml-tog:hover::before { opacity: 1; }
.ml-tog svg { width: 13px; height: 13px; flex-shrink: 0; }
.ml-tog-txt { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; }
.ml-tog.pill { padding: 10px; border-radius: 50%; gap: 0; }
.ml-tog.pill .ml-tog-txt { display: none; }

/* ══ HEADER ══ */
.ml-header {
  height: 70px;
  background: rgba(3,7,16,0.82); backdrop-filter: blur(32px) saturate(180%); -webkit-backdrop-filter: blur(32px) saturate(180%);
  border-bottom: 1px solid rgba(56,189,248,0.07);
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 32px; position: sticky; top: 0; z-index: 30;
}
.ml-header::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg,transparent,rgba(34,211,238,0.09),transparent); pointer-events: none; }

.ml-huser {
  display: flex; align-items: center; gap: 10px;
  background: rgba(14,165,233,0.04); border: 1px solid rgba(56,189,248,0.09);
  border-radius: 14px; padding: 7px 16px 7px 7px;
  cursor: pointer; color: inherit;
  transition: background 0.22s, border-color 0.22s, box-shadow 0.22s, transform 0.22s;
}
.ml-huser:hover { background: rgba(14,165,233,0.1); border-color: var(--b2); box-shadow: 0 4px 22px rgba(14,165,233,0.2); transform: translateY(-2px); }
.ml-hav { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg,#0ea5e9,#22d3ee); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 2px rgba(34,211,238,0.28), 0 3px 14px rgba(14,165,233,0.28); transition: box-shadow 0.25s; }
.ml-huser:hover .ml-hav { box-shadow: 0 0 0 2.5px rgba(34,211,238,0.65), 0 4px 20px rgba(34,211,238,0.42); }
.ml-hav svg { width: 15px; height: 15px; color: #fff; }
.ml-hname { font-family: var(--fs); font-size: 0.88rem; font-weight: 700; color: var(--t1); letter-spacing: -0.01em; }
.ml-hrole { font-family: var(--fm); font-size: 0.62rem; color: var(--t3); }

.ml-hbtn { width: 40px; height: 40px; border-radius: 11px; background: rgba(14,165,233,0.04); border: 1px solid rgba(56,189,248,0.09); display: flex; align-items: center; justify-content: center; color: var(--t3); cursor: pointer; position: relative; transition: all 0.22s; }
.ml-hbtn:hover { background: rgba(14,165,233,0.12); border-color: var(--b2); color: #67e8f9; box-shadow: 0 4px 18px rgba(14,165,233,0.2); transform: translateY(-2px) scale(1.07); }
.ml-hdot { position: absolute; top: 8px; right: 8px; width: 7px; height: 7px; border-radius: 50%; background: var(--cyan4); border: 1.5px solid var(--n900); box-shadow: 0 0 10px var(--cyan4); animation: hdot 2.6s ease-in-out infinite; }
@keyframes hdot { 0%,80%,100%{transform:scale(1);opacity:1} 40%{transform:scale(1.7);opacity:0.4} }

/* Dropdown */
.ml-dd { background: var(--n850)!important; border: 1px solid var(--b2)!important; border-radius: 18px!important; box-shadow: 0 20px 60px rgba(0,0,0,0.82)!important; overflow: hidden; min-width: 220px; font-family: var(--fd)!important; }
.ml-ddhd { padding: 16px 20px 13px; border-bottom: 1px solid rgba(56,189,248,0.07); }
.ml-ddi { padding: 11px 20px!important; font-size: 0.875rem!important; font-family: var(--fd)!important; color: var(--t2)!important; cursor: pointer!important; border-radius: 0!important; transition: background 0.15s,color 0.15s!important; display: flex!important; align-items: center!important; }
.ml-ddi:hover { background: rgba(14,165,233,0.08)!important; color: var(--t1)!important; }
.ml-ddi.danger { color: var(--rose)!important; }
.ml-ddi.danger:hover { background: rgba(251,113,133,0.09)!important; }

/* ══ MOBILE BAR ══ */
.ml-mobbar { position: fixed; top: 0; left: 0; right: 0; height: 62px; background: rgba(3,7,16,0.96); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border-bottom: 1px solid rgba(56,189,248,0.08); display: flex; align-items: center; justify-content: space-between; padding: 0 16px; z-index: 55; }
.ml-mobbtn { width: 40px; height: 40px; border-radius: 11px; background: rgba(14,165,233,0.06); border: 1px solid rgba(56,189,248,0.1); display: flex; align-items: center; justify-content: center; color: var(--t3); cursor: pointer; transition: all 0.2s; }
.ml-mobbtn:hover { background: rgba(14,165,233,0.14); border-color: var(--b2); color: var(--cyan4); }
.ml-mobov { position: fixed; inset: 0; background: rgba(2,5,12,0.88); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); z-index: 48; }

/* ══ RESPONSIVE SHOW / HIDE via pure CSS ══ */
@media (min-width: 1024px) {
  .ml-mobbar       { display: none !important; }
  .ml-mob-sb       { display: none !important; }
  .ml-mobile-main  { display: none !important; }
  .ml-desktop-main { display: flex !important; }
}
@media (max-width: 1023px) {
  /* Hide desktop sidebar and desktop main */
  .ml-sb:not(.ml-mob-sb) { display: none !important; }
  .ml-desktop-main        { display: none !important; }
  /* Show mobile parts */
  .ml-mobbar              { display: flex !important; }
  .ml-mob-sb              { display: flex !important; }
  .ml-mobile-main         { display: block !important; }
}
.ml-mcard {
  position: relative;
  top: 3px; 
}
/* ══ PAGE TRANSITION ══ */
.ml-page { animation: pgIn 0.42s cubic-bezier(0.22,1,0.36,1) both; }
@keyframes pgIn { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }

/* ══ PREVENT HORIZONTAL SCROLL everywhere ══ */
html, body { overflow-x: hidden; max-width: 100vw; }
.ml-main-wrap { overflow-x: hidden; min-width: 0; }
.ml-page-inner { overflow-x: hidden; min-width: 0; }
.ml-page-inner > * { box-sizing: border-box; max-width: 100%; }
`;

/* ── Inject CSS once ── */
let cssInjected = false;
function useGlobalCSS() {
  useEffect(() => {
    if (cssInjected) return;
    cssInjected = true;
    const el = document.createElement("style");
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
  }, []);
}

/* ── Nav item config ── */
const NAV_ITEMS = (t) => [
  { n: t("dashboard"),    I: LayoutDashboard, p: "MerchantDashboard" },
  { n: t("transactions"), I: Receipt,          p: "MerchantTransactions" },
  { n: t("deposits"),     I: ArrowDownToLine,  p: "MerchantDeposit" },
  { n: t("withdrawals"),  I: ArrowUpFromLine,  p: "MerchantWithdrawals" },
  { n: t("providers"),    I: Server,           p: "MerchantProviders", badge: "VIEW" },
  { n: t("routing"),      I: Route,            p: "MerchantRouting" },
  { n: t("webhooks"),     I: Webhook,          p: "MerchantWebhooks" },
  { n: t("settlements"),  I: FileText,         p: "MerchantSettlements" },
  { n: t("notifications"),I: Bell,             p: "Notifications" },
  { n: t("settings"),     I: Settings,         p: "MerchantSettings" },
];

/* ══════════════════════════════════════════
   SIDEBAR INNER — memo so it NEVER re-mounts
   on page navigation (eliminates blink)
══════════════════════════════════════════ */
const SidebarInner = memo(function SidebarInner({
  col, isMobile, nav, currentPage, merchantName, merchantCode, t,
  onLogoClick, onNavClick, onToggle,
}) {
  return (
    <>
      {/* Logo */}
      <Link to={createPageUrl("MerchantDashboard")} onClick={onLogoClick} className="ml-logo">
        <div className="ml-orb"><Store /></div>
        <AnimatePresence initial={false}>
          {(!col || isMobile) && (
            <motion.div key="lt"
              initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-10 }}
              transition={{ duration:0.2 }} style={{ overflow:"hidden" }}>
              <div className="ml-logo-name">1401</div>
              <div className="ml-logo-tag">{t("merchantPortal")}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </Link>

      {/* Nav */}
      <nav className="ml-nav">
        <AnimatePresence initial={false}>
          {(!col || isMobile) && (
            <motion.div key="lbl-main" className="ml-sec-lbl"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.15 }}>
              Navigation
            </motion.div>
          )}
        </AnimatePresence>

        {nav.map((item, idx) => {
          const active = currentPage === item.p;
          const isSettings = item.p === "MerchantSettings";
          return (
            <React.Fragment key={item.p}>
              {isSettings && (
                <>
                  <div className="ml-sep" />
                  <AnimatePresence initial={false}>
                    {(!col || isMobile) && (
                      <motion.div key="lbl-cfg" className="ml-sec-lbl"
                        initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.15 }}>
                        Config
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}

              <div className={(col && !isMobile) ? "ml-tipwrap" : undefined}>
                <Link
                  to={createPageUrl(item.p)}
                  onClick={(e) => onNavClick(item.p, e)}
                  className={`ml-item${active ? " on" : ""}`}
                  style={(col && !isMobile) ? { justifyContent:"center", padding:"0", height:46, borderRadius:12 } : {}}
                >
                  <div className="ml-ico"><item.I /></div>
                  <AnimatePresence initial={false}>
                    {(!col || isMobile) && (
                      <motion.div key={`l-${item.p}`}
                        initial={{ opacity:0, width:0 }} animate={{ opacity:1, width:"auto" }} exit={{ opacity:0, width:0 }}
                        transition={{ duration:0.18 }}
                        style={{ display:"flex", alignItems:"center", gap:8, overflow:"hidden", flex:1 }}>
                        <span className="ml-item-txt">{item.n}</span>
                        {item.badge && <span className="ml-badge">{item.badge}</span>}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Link>
                {(col && !isMobile) && <div className="ml-tip">{item.n}{item.badge ? ` · ${item.badge}` : ""}</div>}
              </div>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Merchant Card */}
      <AnimatePresence initial={false}>
        {(!col || isMobile) && (
          <motion.div key="mcard"
            initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }} exit={{ opacity:0, height:0 }}
            transition={{ duration:0.26 }} style={{ overflow:"hidden" }}>
            <div className="ml-mcard">
              <div className="ml-mcard-in">
                <div className="ml-mcard-top">
                  <div className="ml-mcard-av"><Store /></div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div className="ml-mcard-lbl">Logged in as</div>
                    <div className="ml-mcard-name">{merchantName}</div>
                  </div>
                </div>
                <div className="ml-mcard-div" />
                <div className="ml-mcard-bot">
                  {merchantCode
                    ? <><span className="ml-mcard-id-lbl">Code</span><span className="ml-mcard-id-val">{merchantCode}</span></>
                    : <><span className="ml-mcard-id-lbl">Role</span><span className="ml-mcard-id-val">Merchant</span></>}
                  <div className="ml-mcard-status"><div className="ml-mcard-dot" /><span>Active</span></div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapse button — desktop only */}
      {!isMobile && (
        <div className="ml-togwrap">
          <motion.button
            className={`ml-tog${col ? " pill" : ""}`}
            onClick={onToggle}
            whileTap={{ scale:0.92 }}
            title={col ? "Expand sidebar" : "Collapse sidebar"}
          >
            <motion.div animate={{ rotate: col ? 180 : 0 }} transition={{ duration:0.38, ease:[0.34,1.56,0.64,1] }}>
              <ChevronLeft size={14} />
            </motion.div>
            {!col && <span className="ml-tog-txt">Collapse</span>}
          </motion.button>
        </div>
      )}
    </>
  );
});

/* ══════════════════════════════════════════
   MAIN LAYOUT — CSS-only responsive (no JS isMobile state)
   This eliminates ALL sidebar blink issues since sidebar never unmounts
══════════════════════════════════════════ */
export default function MerchantLayout({
  children, currentPage,
  merchantName = "Royal Gaming", merchantCode = "",
}) {
  useGlobalCSS();

  const [col,    setCol]    = useState(false);
  const [mob,    setMob]    = useState(false);  // mobile drawer open
  const navigate            = useNavigate();
  const { t }               = useLanguage();
  const nav                 = NAV_ITEMS(t);

  const logout = () => { auth.logout(); window.location.href = "/MerchantLogin"; };

  // Mobile nav: close drawer first, then navigate — NO setState during navigation
  const handleNavClick = useCallback((targetPage, e) => {
    // Only intercept if mobile drawer is open
    if (!mob) return;
    e.preventDefault();
    // Close drawer without any other state change
    setMob(false);
    // Navigate after drawer CSS transition completes
    setTimeout(() => navigate(createPageUrl(targetPage)), 320);
  }, [mob, navigate]);

  const handleLogoClick = useCallback((e) => {
    if (!mob) return;
    e.preventDefault();
    setMob(false);
    setTimeout(() => navigate(createPageUrl("MerchantDashboard")), 320);
  }, [mob, navigate]);

  const sidebarW = col ? 68 : 258;

  return (
    <div style={{ minHeight:"100vh", background:"var(--n950)", overflowX:"hidden" }}>

      {/* ── MOBILE TOP BAR (CSS hidden on desktop) ── */}
      <div className="ml-mobbar">
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button className="ml-mobbtn" onClick={()=>setMob(v=>!v)} aria-label="Toggle menu">
            <AnimatePresence mode="wait" initial={false}>
              {mob
                ? <motion.span key="x" initial={{ rotate:-90,opacity:0 }} animate={{ rotate:0,opacity:1 }} exit={{ rotate:90,opacity:0 }} transition={{ duration:0.14 }}><X size={17}/></motion.span>
                : <motion.span key="m" initial={{ rotate:90,opacity:0 }} animate={{ rotate:0,opacity:1 }} exit={{ rotate:-90,opacity:0 }} transition={{ duration:0.14 }}><Menu size={17}/></motion.span>}
            </AnimatePresence>
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div className="ml-orb" style={{ width:34, height:34, borderRadius:10 }}><Store /></div>
            <span style={{ fontFamily:"var(--fs)", fontWeight:800, fontSize:"1.05rem", color:"var(--t1)" }}>1401</span>
          </div>
        </div>
        <div className="ml-hbtn" style={{ position:"relative" }}>
          <Bell size={17}/><div className="ml-hdot"/>
        </div>
      </div>

      {/* ── MOBILE OVERLAY ── */}
      <AnimatePresence>
        {mob && (
          <motion.div className="ml-mobov"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            transition={{ duration:0.2 }} onClick={()=>setMob(false)} />
        )}
      </AnimatePresence>

      {/* ── SIDEBAR ──
          ONE sidebar always mounted — CSS controls visibility/position.
          Desktop: fixed left, width transitions via CSS.
          Mobile: separate mobile sidebar slides in/out.
          Never unmounts → zero blink on navigation.
      ── */}

      {/* Sidebar content — always mounted, never re-mounts */}
      <aside
        className="ml-sb"
        style={{
          width: sidebarW,
          transition: "width 0.38s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <SidebarInner
          col={col} isMobile={false}
          nav={nav} currentPage={currentPage}
          merchantName={merchantName} merchantCode={merchantCode}
          t={t}
          onLogoClick={handleLogoClick}
          onNavClick={handleNavClick}
          onToggle={() => setCol(v=>!v)}
        />
      </aside>

      {/* ── DESKTOP MAIN (hidden on mobile via CSS) ── */}
      <div
        className="ml-main-wrap ml-desktop-main"
        style={{ marginLeft:sidebarW, transition:"margin-left 0.38s cubic-bezier(0.22,1,0.36,1)", minHeight:"100vh", display:"flex", flexDirection:"column", overflowX:"hidden" }}
      >
        <header className="ml-header">
          <div style={{ flex:1 }} />
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <LanguageSwitcher variant="ghost" />
            <NotificationDropdown accentColor="blue" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-huser">
                  <div className="ml-hav"><User /></div>
                  <div style={{ textAlign:"left" }}>
                    <div className="ml-hname">{merchantName}</div>
                    <div className="ml-hrole">{t("merchant")}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="ml-dd">
                <div className="ml-ddhd">
                  <div style={{ fontFamily:"var(--fs)", fontWeight:700, color:"var(--t1)", fontSize:"0.92rem" }}>{merchantName}</div>
                  <div style={{ fontFamily:"var(--fm)", fontSize:"0.65rem", color:"var(--t4)", marginTop:4 }}>{merchantCode || "Merchant"}</div>
                </div>
                <DropdownMenuItem className="ml-ddi" onClick={()=>navigate(createPageUrl("MerchantSettings"))}>
                  <Settings style={{ width:14, height:14, marginRight:10, opacity:0.5 }} />{t("settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator style={{ background:"rgba(56,189,248,0.07)" }} />
                <DropdownMenuItem className="ml-ddi danger" onClick={logout}>
                  <LogOut style={{ width:14, height:14, marginRight:10 }} />{t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main key={currentPage} className="ml-page ml-page-inner" style={{ flex:1, padding:"28px 32px 60px", overflowX:"hidden" }}>
          {children}
        </main>
      </div>

      {/* ── MOBILE SIDEBAR (separate, CSS controlled) ── */}
      <aside
        className="ml-sb ml-mob-sb"
        style={{
          width: 270,
          transform: mob ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.32s cubic-bezier(0.22,1,0.36,1)",
          zIndex: 52,
        }}
      >
        <SidebarInner
          col={false} isMobile={true}
          nav={nav} currentPage={currentPage}
          merchantName={merchantName} merchantCode={merchantCode}
          t={t}
          onLogoClick={handleLogoClick}
          onNavClick={handleNavClick}
          onToggle={() => setMob(false)}
        />
      </aside>

      {/* ── MOBILE MAIN ── */}
      <div
        className="ml-main-wrap ml-mobile-main"
        key={currentPage}
        style={{ paddingTop:62, overflowX:"hidden" }}
      >
        <div className="ml-page ml-page-inner" style={{ padding:"16px 14px 60px", overflowX:"hidden" }}>
          {children}
        </div>
      </div>
    </div>
  );
}